import { FC, useEffect, useState } from "react"
import dayjs, { Dayjs } from "dayjs"
import { useDispatch, useSelector } from "react-redux"
import { useParams } from "react-router"
import { LevelVitalsResponse } from "@/api/FacilityLevelsService"
import OccupancyHeatMap from "@/components/OccupancyHeatMap"
import DateRangePicker from "@/components/shared/DateRangePicker"
import Grid from "@/components/shared/Grid"
import GridItem from "@/components/shared/Grid/GridItem"
import VitalSummaryCard from "@/components/VitalSummaryCard"
import VitalTrendChart from "@/components/VitalTrendChart"
import { AppDispatch, RootState } from "@/store"
import {
   selectMeasurementSystem,
   selectTimezone,
} from "@/store/facilitiesSlice"
import {
   fetchLevelVitals,
   fetchLevelVitalsGraph,
   resetLevelVitals,
   resetLevelVitalsGraph,
} from "@/store/facilityLevelsSlice"
import { getErrorMessage, localizeDateTime } from "@/utils/index"
import { convertDistance, getDistanceUnit } from "@/utils/measurementSystem"

const GRAPH_MAX_RANGE_DAYS = 7

const Summary: FC = () => {
   const dispatch = useDispatch<AppDispatch>()
   const [lastRead, setLastRead] = useState<Dayjs | null>(null)
   const { id } = useParams<{ id: string; alertId: string }>()
   const [graphFrom, setGraphFrom] = useState<Dayjs>(() =>
      dayjs().subtract(1, "day")
   )
   const [graphTo, setGraphTo] = useState<Dayjs>(() => dayjs())
   const timezone = useSelector(selectTimezone)
   const measurementSystem = useSelector(selectMeasurementSystem)
   const {
      facilityLevelVitals,
      loadingFacilityLevelVitals,
      errorFacilityLevelVitals,
      facilityLevelVitalsGraph,
      loadingFacilityLevelVitalsGraph,
      errorFacilityLevelVitalsGraph,
   } = useSelector(
      ({ facilityLevelsReducer }: RootState) => facilityLevelsReducer
   )
   const {
      averages,
      heartRate,
      heartRateStabilityScore,
      moving,
      respiratoryRate,
      respiratoryStabilityScore,
      distance,
   } = facilityLevelVitals || ({} as LevelVitalsResponse)
   const {
      heartRate: heartRateAverage,
      respiratoryRate: respiratoryRateAverage,
      totalCountHr,
      totalCountRr,
   } = averages || ({} as LevelVitalsResponse["averages"])
   const radarDistanceLabel = distance
      ? `${convertDistance(distance, measurementSystem)?.toFixed(1)} ${getDistanceUnit(measurementSystem)}`
      : null

   useEffect(() => {
      async function fetchLevelVitalsData() {
         try {
            if (!id) return
            await dispatch(fetchLevelVitals(id))
               .unwrap()
               .then(() => {
                  setLastRead(dayjs())
               })
         } catch (err) {
            console.error("Level vitals fetch failed:", err)
         }
      }

      fetchLevelVitalsData()
      const interval = setInterval(fetchLevelVitalsData, 5000)

      return () => {
         clearInterval(interval)
         dispatch(resetLevelVitals())
      }
   }, [dispatch, id])

   useEffect(() => {
      if (!id || !timezone) return

      dispatch(
         fetchLevelVitalsGraph({
            levelId: id,
            startDate: graphFrom.tz(timezone).format("YYYY-MM-DD"),
            endDate: graphTo.tz(timezone).format("YYYY-MM-DD"),
            timezone,
         })
      ).catch((err) => {
         console.error("Level vitals graph fetch failed:", err)
      })

      return () => {
         dispatch(resetLevelVitalsGraph())
      }
   }, [dispatch, id, graphFrom, graphTo, timezone])

   return (
      <Grid className="cell-detail-overview__summary">
         {!loadingFacilityLevelVitals && errorFacilityLevelVitals ? (
            <GridItem size={12}>
               {getErrorMessage(errorFacilityLevelVitals)}
            </GridItem>
         ) : (
            <>
               {lastRead && (
                  <>
                     <span className="cell-detail-overview__last-read">
                        Last read:&nbsp;
                        <span>{localizeDateTime(lastRead, timezone)}</span>
                     </span>
                  </>
               )}
               {facilityLevelVitals !== null && (
                  <>
                     {radarDistanceLabel !== null && (
                        <span className="cell-detail-overview__distance">
                           Radar distance:&nbsp;
                           <span>{radarDistanceLabel}</span>
                        </span>
                     )}
                     <GridItem
                        xs={12}
                        sm={12}
                        md={6}
                        lg={6}
                     >
                        <VitalSummaryCard
                           iconName="heartRate"
                           title="Heart Rate"
                           rateTitle="BPM"
                           inmateMoving={moving}
                           vital={heartRate}
                           vitalRate={heartRateAverage}
                           captureQuality={heartRateStabilityScore}
                           totalCount={totalCountHr}
                        />
                     </GridItem>
                     <GridItem
                        xs={12}
                        sm={12}
                        md={6}
                        lg={6}
                     >
                        <VitalSummaryCard
                           iconName="respiratoryRate"
                           title="Respiratory Rate"
                           rateTitle="RPM"
                           inmateMoving={moving}
                           vital={respiratoryRate}
                           vitalRate={respiratoryRateAverage}
                           captureQuality={respiratoryStabilityScore}
                           totalCount={totalCountRr}
                        />
                     </GridItem>

                     {timezone && (
                        <>
                           <GridItem
                              xs={12}
                              sm={12}
                              md={12}
                              lg={12}
                           >
                              <div className="cell-detail-overview__graph-controls">
                                 <DateRangePicker
                                    from={graphFrom}
                                    to={graphTo}
                                    timezone={timezone}
                                    onChange={({ from, to }) => {
                                       setGraphFrom(from)
                                       setGraphTo(to)
                                    }}
                                    maxRangeDays={GRAPH_MAX_RANGE_DAYS}
                                 />
                                 {!loadingFacilityLevelVitalsGraph &&
                                    errorFacilityLevelVitalsGraph && (
                                       <span className="cell-detail-overview__graph--error">
                                          {getErrorMessage(
                                             errorFacilityLevelVitalsGraph,
                                             "Selected date range is too large. Please choose 7 days or fewer."
                                          )}
                                       </span>
                                    )}
                              </div>
                           </GridItem>
                           <GridItem
                              xs={12}
                              sm={12}
                              md={12}
                              lg={12}
                           >
                              <VitalTrendChart
                                 data={facilityLevelVitalsGraph}
                                 loading={loadingFacilityLevelVitalsGraph}
                                 timezone={timezone}
                              />
                           </GridItem>
                           <GridItem
                              xs={12}
                              sm={12}
                              md={12}
                              lg={12}
                           >
                              <OccupancyHeatMap
                                 data={facilityLevelVitalsGraph}
                                 loading={loadingFacilityLevelVitalsGraph}
                                 timezone={timezone}
                              />
                           </GridItem>
                        </>
                     )}
                  </>
               )}
            </>
         )}
      </Grid>
   )
}

export default Summary
